/**
 * Messages to the embedding page of the Polaris embed (lib/embed-theme.ts).
 *
 * The Shopify admin app shows the Creator inside its own page and needs three
 * facts from it: the Creator is up, how tall it is (the host sizes the iframe,
 * so there is no second scrollbar) and that a design was published.
 *
 *   { type: 'designdeck:embed:ready' }
 *   { type: 'designdeck:embed:resize', height: number }   // CSS px, integer
 *   { type: 'designdeck:embed:published', designId: string }
 *   { type: 'designdeck:embed:bulk-created', totalProducts: number }
 *
 * Sent only under the Polaris embedding theme — other embedders (Shirtee
 * Cloud, marketplaces) see no change. The target origin is the origin of
 * `document.referrer`, i.e. the page that loaded this iframe, never `*`: a
 * page without a referrer (or with one we cannot parse) gets nothing.
 */

import { isPolarisEmbed } from './embed-theme';

export type EmbedMessage =
  | { type: 'designdeck:embed:ready' }
  | { type: 'designdeck:embed:resize'; height: number }
  | { type: 'designdeck:embed:published'; designId: string }
  | { type: 'designdeck:embed:bulk-created'; totalProducts: number };

/** Origin of the embedding page, or null when it is unknown. */
export function embedderOrigin(): string | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

/** Posts `message` to the parent window; a no-op outside the Polaris embed. */
export function postEmbedMessage(message: EmbedMessage): boolean {
  if (typeof window === 'undefined' || window.parent === window) return false;
  if (!isPolarisEmbed()) return false;
  const origin = embedderOrigin();
  if (!origin) return false;
  window.parent.postMessage(message, origin);
  return true;
}

/**
 * Reports the document height to the parent whenever it changes, at most once
 * per animation frame. Returns the cleanup. Without the Polaris embed it does
 * nothing (and observes nothing).
 */
export function observeEmbedHeight(): () => void {
  if (
    typeof window === 'undefined' ||
    typeof ResizeObserver === 'undefined' ||
    !isPolarisEmbed()
  ) {
    return () => undefined;
  }
  const root = document.documentElement;
  let lastHeight = -1;
  let pending = false;
  let frame: number | null = null;

  const report = () => {
    pending = false;
    frame = null;
    const height = Math.ceil(root.scrollHeight);
    if (height === lastHeight) return;
    lastHeight = height;
    postEmbedMessage({ type: 'designdeck:embed:resize', height });
  };
  const schedule = () => {
    if (pending) return;
    // Flag first: a frame callback may run before requestAnimationFrame returns.
    pending = true;
    const id = window.requestAnimationFrame(report);
    if (pending) frame = id;
  };

  const observer = new ResizeObserver(schedule);
  observer.observe(root);
  schedule();
  return () => {
    observer.disconnect();
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
}
